/**
 * BLRRD pixel-layer serialisation — Phase 4.A.layers.1.
 *
 * Snapshot/restore plumbing. Each pixel layer is encoded as a plain
 * JSON-friendly object with its Float32 RGBA buffer (and optional mask)
 * base64-encoded. The encoding is intentionally simple for v1; Phase
 * 7.1.1 follow-up may switch to per-layer PNG sidecars in IndexedDB to
 * keep snapshot strings small.
 *
 * Format version note:
 *   Wave 7.1 stamps every serialised record with version: 1. Future
 *   formats bump the version and `deserializeLayers` dispatches; old
 *   snapshots never break.
 */

import { createPixelLayer, isPixelLayer } from './model.js';

const SERIALIZATION_VERSION = 1;

/* Base64 encoders that work in both browser (where `btoa` exists) and
 * Node (where Buffer does). Tests run pure-Node, so we need the Buffer
 * fallback. Detection is done once at module load. */
const _hasBufferGlobal = typeof globalThis !== 'undefined'
    && typeof globalThis.Buffer === 'function'
    && typeof globalThis.Buffer.from === 'function';

function bytesToBase64(uint8) {
    if (_hasBufferGlobal) {
        return globalThis.Buffer.from(uint8.buffer, uint8.byteOffset, uint8.byteLength).toString('base64');
    }
    /* Browser path — btoa requires a binary string. Chunked to avoid
     * the "argument list too long" overflow on very large layers. */
    let bin = '';
    const CHUNK = 0x8000;
    for (let i = 0; i < uint8.length; i += CHUNK) {
        bin += String.fromCharCode.apply(null, uint8.subarray(i, Math.min(i + CHUNK, uint8.length)));
    }
    return btoa(bin);
}

function base64ToBytes(b64) {
    if (_hasBufferGlobal) {
        const buf = globalThis.Buffer.from(b64, 'base64');
        const out = new Uint8Array(buf.byteLength);
        out.set(buf);
        return out;
    }
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i) & 0xFF;
    return out;
}

function f32ToBase64(arr) {
    if (!(arr instanceof Float32Array)) return null;
    const view = new Uint8Array(arr.buffer, arr.byteOffset, arr.byteLength);
    return bytesToBase64(view);
}

function base64ToF32(b64, expectedLen) {
    if (typeof b64 !== 'string' || b64.length === 0) return null;
    const bytes = base64ToBytes(b64);
    if (bytes.byteLength !== expectedLen * 4) {
        throw new Error(
            `base64ToF32: byte length mismatch (got ${bytes.byteLength}, want ${expectedLen * 4})`,
        );
    }
    /* Copy into a fresh ArrayBuffer so the Float32Array view aligns on
     * a 4-byte boundary regardless of the upstream byteOffset. */
    const ab = new ArrayBuffer(bytes.byteLength);
    new Uint8Array(ab).set(bytes);
    return new Float32Array(ab);
}

/**
 * Serialise a layer stack. Non-pixel layers (legacy parametric overlays)
 * are passed through to the existing snapshot path; this function only
 * encodes the pixel kind.
 *
 * @param {Array<object>} layers
 * @returns {Array<object>} JSON-friendly records
 */
export function serializeLayers(layers) {
    if (!Array.isArray(layers)) return [];
    const out = [];
    for (let i = 0; i < layers.length; i++) {
        const l = layers[i];
        if (!isPixelLayer(l)) continue;
        const w = l.w | 0, h = l.h | 0;
        out.push({
            version: SERIALIZATION_VERSION,
            kind:    'pixel',
            id:      l.id,
            name:    l.name,
            visible: l.visible !== false,
            opacity: l.opacity,
            blendMode: l.blendMode,
            w, h,
            offsetX: l.offsetX | 0,
            offsetY: l.offsetY | 0,
            rgbaBase64: f32ToBase64(l.rgba),
            maskBase64: l.mask ? f32ToBase64(l.mask.rgba) : null,
            maskW: l.mask ? l.mask.w | 0 : 0,
            maskH: l.mask ? l.mask.h | 0 : 0,
        });
    }
    return out;
}

/**
 * Deserialise a previously-serialised stack. Returns fresh pixel-layer
 * records (no shared buffers with the snapshot blob). Tolerates missing
 * fields per the version-1 schema; unknown future versions return [].
 *
 * @param {Array<object>} serialized
 * @returns {Array<object>} pixel-layer records
 */
export function deserializeLayers(serialized) {
    if (!Array.isArray(serialized)) return [];
    const out = [];
    for (let i = 0; i < serialized.length; i++) {
        const rec = serialized[i];
        if (!rec || typeof rec !== 'object') continue;
        if (rec.version !== SERIALIZATION_VERSION) continue;
        if (rec.kind !== 'pixel') continue;
        const w = rec.w | 0, h = rec.h | 0;
        if (w <= 0 || h <= 0) continue;
        let rgba;
        try {
            rgba = base64ToF32(rec.rgbaBase64, w * h * 4);
        } catch {
            continue;
        }
        if (!rgba) continue;
        let mask = null;
        if (rec.maskBase64 && rec.maskW && rec.maskH) {
            const mw = rec.maskW | 0, mh = rec.maskH | 0;
            try {
                const mrgba = base64ToF32(rec.maskBase64, mw * mh);
                if (mrgba) mask = { rgba: mrgba, w: mw, h: mh };
            } catch {
                mask = null;
            }
        }
        try {
            out.push(createPixelLayer({
                id: rec.id,
                name: rec.name,
                w, h,
                opacity: rec.opacity,
                blendMode: rec.blendMode,
                rgba,
                mask,
                offsetX: rec.offsetX,
                offsetY: rec.offsetY,
                visible: rec.visible,
            }));
        } catch {
            /* skip malformed record */
        }
    }
    return out;
}

/** Surface the version constant for tests + future migrations. */
export { SERIALIZATION_VERSION };
